import { atom } from 'jotai';
import { WS_EVENTS } from '@twinden/shared';
import type {
  FamilyDocument,
  FamilyLink,
  FamilyList,
  FamilyListItem,
  ListCounts,
  StarredMessage,
} from '../lib/api';

type ByFamily<T> = Record<string, T[]>;

// Family-app caches (family-apps spec §9). Everything is keyed by family id so
// switching families re-scopes instantly; WS events update the caches by
// entity id, which makes replaying an event idempotent.
export const linksByFamilyAtom = atom<ByFamily<FamilyLink>>({});

export const documentsByFamilyAtom = atom<ByFamily<FamilyDocument>>({});

export const listsByFamilyAtom = atom<ByFamily<FamilyList>>({});

export const listItemsByListAtom = atom<Record<string, FamilyListItem[]>>({});

export const starredByFamilyAtom = atom<ByFamily<StarredMessage>>({});

function upsertById<T extends { id: string }>(items: T[], item: T): T[] {
  const index = items.findIndex((existing) => existing.id === item.id);
  if (index === -1) return [item, ...items];
  const next = [...items];
  next[index] = { ...next[index], ...item };
  return next;
}

function byCreatedAtDesc<T extends { createdAt: string }>(items: T[]): T[] {
  return [...items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

function byPosition<T extends { position: number; createdAt: string }>(
  items: T[]
): T[] {
  return [...items].sort(
    (a, b) =>
      a.position - b.position ||
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

function byUpdatedAtDesc(lists: FamilyList[]): FamilyList[] {
  return [...lists].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

interface AppEvent {
  event: string;
  payload: unknown;
}

// Applies one family-app WS event to the caches. Unknown events are ignored.
export const applyFamilyAppEventAtom = atom(
  null,
  (get, set, { event, payload }: AppEvent) => {
    switch (event) {
      case WS_EVENTS.LINK_ADDED: {
        const data = payload as { familyId: string; link: FamilyLink };
        const links = get(linksByFamilyAtom);
        const current = links[data.familyId] ?? [];
        set(linksByFamilyAtom, {
          ...links,
          [data.familyId]: byCreatedAtDesc(upsertById(current, data.link)),
        });
        break;
      }

      case WS_EVENTS.LINK_REMOVED: {
        const data = payload as { familyId: string; linkId: string };
        const links = get(linksByFamilyAtom);
        set(linksByFamilyAtom, {
          ...links,
          [data.familyId]: (links[data.familyId] ?? []).filter(
            (link) => link.id !== data.linkId
          ),
        });
        break;
      }

      case WS_EVENTS.DOCUMENT_ADDED: {
        const data = payload as {
          familyId: string;
          document: FamilyDocument;
        };
        const documents = get(documentsByFamilyAtom);
        const current = documents[data.familyId] ?? [];
        set(documentsByFamilyAtom, {
          ...documents,
          [data.familyId]: byCreatedAtDesc(upsertById(current, data.document)),
        });
        break;
      }

      // M26: folder, tags or note changed (D49).
      case WS_EVENTS.DOCUMENT_UPDATED: {
        const data = payload as {
          familyId: string;
          document: FamilyDocument | null;
        };
        if (!data.document) break;
        const documents = get(documentsByFamilyAtom);
        const current = documents[data.familyId] ?? [];
        set(documentsByFamilyAtom, {
          ...documents,
          [data.familyId]: current.map((document) =>
            document.id === data.document!.id ? data.document! : document
          ),
        });
        break;
      }

      case WS_EVENTS.DOCUMENT_REMOVED: {
        const data = payload as { familyId: string; documentId: string };
        const documents = get(documentsByFamilyAtom);
        set(documentsByFamilyAtom, {
          ...documents,
          [data.familyId]: (documents[data.familyId] ?? []).filter(
            (document) => document.id !== data.documentId
          ),
        });
        break;
      }

      case WS_EVENTS.LIST_CREATED:
      case WS_EVENTS.LIST_UPDATED: {
        const data = payload as { familyId: string; list: FamilyList };
        const lists = get(listsByFamilyAtom);
        const current = lists[data.familyId] ?? [];
        set(listsByFamilyAtom, {
          ...lists,
          [data.familyId]: byUpdatedAtDesc(upsertById(current, data.list)),
        });
        break;
      }

      case WS_EVENTS.LIST_DELETED: {
        const data = payload as { familyId: string; listId: string };
        const lists = get(listsByFamilyAtom);
        const items = get(listItemsByListAtom);
        const nextItems = { ...items };
        delete nextItems[data.listId];
        set(listItemsByListAtom, nextItems);
        set(listsByFamilyAtom, {
          ...lists,
          [data.familyId]: (lists[data.familyId] ?? []).filter(
            (list) => list.id !== data.listId
          ),
        });
        break;
      }

      case WS_EVENTS.LIST_ITEM_ADDED:
      case WS_EVENTS.LIST_ITEM_UPDATED: {
        const data = payload as {
          familyId: string;
          listId: string;
          item: FamilyListItem;
          counts: ListCounts;
        };
        const items = get(listItemsByListAtom);
        const current = items[data.listId] ?? [];
        set(listItemsByListAtom, {
          ...items,
          [data.listId]: byPosition(upsertById(current, data.item)),
        });

        const lists = get(listsByFamilyAtom);
        set(listsByFamilyAtom, {
          ...lists,
          [data.familyId]: (lists[data.familyId] ?? []).map((list) =>
            list.id === data.listId
              ? {
                  ...list,
                  itemCount: data.counts.total,
                  checkedCount: data.counts.checked,
                }
              : list
          ),
        });
        break;
      }

      case WS_EVENTS.LIST_ITEM_DELETED: {
        const data = payload as {
          familyId: string;
          listId: string;
          itemId: string;
          counts: ListCounts;
        };
        const items = get(listItemsByListAtom);
        set(listItemsByListAtom, {
          ...items,
          [data.listId]: (items[data.listId] ?? []).filter(
            (item) => item.id !== data.itemId
          ),
        });

        const lists = get(listsByFamilyAtom);
        set(listsByFamilyAtom, {
          ...lists,
          [data.familyId]: (lists[data.familyId] ?? []).map((list) =>
            list.id === data.listId
              ? {
                  ...list,
                  itemCount: data.counts.total,
                  checkedCount: data.counts.checked,
                }
              : list
          ),
        });
        break;
      }

      default:
        break;
    }
  }
);
